import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  myTeamFiltersService,
  type MyTeamSection,
  type MyTeamCompany,
  type MyTeamEmployee,
} from '@/api/domains/attendance/myTeam';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';
import { toStringArray } from '@/utils';


// Local (non-Redux) state backing the "Mi equipo" filters panel. [] means
// "sin selección explícita" which, per business rule, is equivalent to "todas
// las opciones seleccionadas" for that filter.
export interface MyTeamFilterState {
  sectionCodes: string[];
  companyIds: string[];
  employeeIds: string[];
}

const DEFAULT_FILTER_STATE: MyTeamFilterState = {
  sectionCodes: [],
  companyIds: [],
  employeeIds: [],
};

type SelectOption = { value: string; label: string };

export interface UseMyTeamFiltersResult {
  isLoading: boolean;
  isEmployeesLoading: boolean;
  filters: MyTeamFilterState;
  // Raw sections catalog (code + backend-provided label + isDefault), used to render
  // one placeholder container per section without hardcoding any section code.
  sections: MyTeamSection[];
  catalogOptions: {
    sectionOptions: SelectOption[];
    companyOptions: SelectOption[];
    employeeOptions: SelectOption[];
  };
  // Effective section codes considering the "no selection = all" rule. Used to
  // decide which placeholder section containers are visible.
  visibleSectionCodes: string[];
  handlers: {
    onSectionChange: (codes: string | string[]) => void;
    onCompanyChange: (ids: string | string[]) => void;
    onEmployeeChange: (ids: string | string[]) => void;
  };
  refetch: () => void;
}

function toNumbers(ids: string[]): number[] {
  return ids.map(Number).filter((n) => !Number.isNaN(n));
}

export function useMyTeamFilters(): UseMyTeamFiltersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal: createFiltersSignal } = useAbortController();
  const { createSignal: createEmployeesSignal } = useAbortController();

  const [isLoading, setIsLoading] = useState(true);
  const [isEmployeesLoading, setIsEmployeesLoading] = useState(false);
  const [sections, setSections] = useState<MyTeamSection[]>([]);
  const [companies, setCompanies] = useState<MyTeamCompany[]>([]);
  const [employees, setEmployees] = useState<MyTeamEmployee[]>([]);
  const [filters, setFilters] = useState<MyTeamFilterState>(DEFAULT_FILTER_STATE);

  // Guards the employees-by-company effect so it does not refetch right after the
  // initial catalogs load (that response already carries the unfiltered employees).
  const hasLoadedInitialFilters = useRef(false);

  // Carga (inicial y bajo demanda) de secciones, empresas y colaboradores desde el mismo
  // GET /attendance/my-team/filters. Siembra la selección de Secciones con la sección
  // `isDefault` provista por el backend (por regla de negocio) cada vez que se ejecuta.
  const loadFilters = useCallback(() => {
    const signal = createFiltersSignal();
    setIsLoading(true);

    void (async () => {
      const response = await myTeamFiltersService.getFilters(signal);
      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.myTeam.filters.loadError'));
        return;
      }

      const {
        sections: loadedSections,
        companies: loadedCompanies,
        employees: loadedEmployees,
      } = response.data;
      setSections(loadedSections);
      setCompanies(loadedCompanies);
      setEmployees(loadedEmployees);

      const defaultSection = loadedSections.find((section) => section.isDefault);
      setFilters((previous) => ({
        ...previous,
        sectionCodes: defaultSection ? [defaultSection.code] : [],
      }));

      hasLoadedInitialFilters.current = true;
    })();
  }, [createFiltersSignal, showApiError, translate]);

  // Carga inicial al montar el componente.
  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  // Recalculates the employees catalog whenever the Empresas selection changes, and
  // drops any previously selected employee that no longer belongs to the new catalog.
  useEffect(() => {
    if (!hasLoadedInitialFilters.current) return;

    const signal = createEmployeesSignal();
    setIsEmployeesLoading(true);
    const companyIdNumbers = toNumbers(filters.companyIds);

    void (async () => {
      const response = await myTeamFiltersService.getEmployees(
        companyIdNumbers.length > 0 ? { companyIds: companyIdNumbers } : undefined,
        signal,
      );
      if (response.error?.code === 'ABORTED') return;
      setIsEmployeesLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.myTeam.filters.employeesLoadError'));
        return;
      }

      setEmployees(response.data);
      const validEmployeeIds = new Set(response.data.map((employee) => String(employee.id)));
      setFilters((previous) => ({
        ...previous,
        employeeIds: previous.employeeIds.filter((id) => validEmployeeIds.has(id)),
      }));
    })();
  }, [filters.companyIds, createEmployeesSignal, showApiError, translate]);

  const onSectionChange = useCallback((codes: string | string[]) => {
    setFilters((previous) => ({ ...previous, sectionCodes: toStringArray(codes) }));
  }, []);

  const onCompanyChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, companyIds: toStringArray(ids) }));
  }, []);

  const onEmployeeChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, employeeIds: toStringArray(ids) }));
  }, []);

  const sectionOptions = useMemo<SelectOption[]>(
    () => sections.map((section) => ({ value: section.code, label: section.label })),
    [sections],
  );

  const companyOptions = useMemo<SelectOption[]>(
    () => companies.map((company) => ({ value: String(company.id), label: company.name })),
    [companies],
  );

  const employeeOptions = useMemo<SelectOption[]>(
    () => employees.map((employee) => ({ value: String(employee.id), label: employee.name })),
    [employees],
  );

  // "Sin selección explícita" => todas las secciones se consideran seleccionadas.
  const visibleSectionCodes = useMemo(
    () => (
      filters.sectionCodes.length > 0
        ? filters.sectionCodes
        : sections.map((section) => section.code)
    ),
    [filters.sectionCodes, sections],
  );

  return {
    isLoading,
    isEmployeesLoading,
    filters,
    sections,
    catalogOptions: {
      sectionOptions,
      companyOptions,
      employeeOptions,
    },
    visibleSectionCodes,
    handlers: {
      onSectionChange,
      onCompanyChange,
      onEmployeeChange,
    },
    refetch: loadFilters,
  };
}

export default useMyTeamFilters;
